import { IndexedArray } from 'utils/arrays/arrayUtils';
import Order from 'communication/types/order/Order';

export enum OrderFilterResultsChangedEventType {
    /*
     * The complete membership has changed - consuming widgets calculate using the full set of results (rather than the delta)
     * i.e. a filter has changed, or active input has changed
     */
    RESET = 'Reset',
    /*
     * An update to an order (or addition or removal) has caused this change. Consuming widgets may just apply the delta.
     */
    UPDATED = 'Updated',
}

export interface OrderFilterResultsChangedEventMemberUpdate {
    prev?: Order;
    next?: Order;
}

export interface OrderFilterResultsChangedEventUpdates {
    newMembers: IndexedArray<OrderFilterResultsChangedEventMemberUpdate>;
    removedMembers: IndexedArray<OrderFilterResultsChangedEventMemberUpdate>;
    updatedMembers: IndexedArray<OrderFilterResultsChangedEventMemberUpdate>;
}

/*
 * "Event" action to indicate that the orders stream reset or was updated
 * Dispatched asyncronously to enable consumption by middleware
 */
export interface OrderFilterResultsChangedEvent {
    changedType: OrderFilterResultsChangedEventType;
    batchUpdateInProgress?: boolean;

    /*
     * If the type is UPDATED, this has details of the updates
     */
    updates?: OrderFilterResultsChangedEventUpdates;
}

export class OrderFilterResultsChangedEventBuilder {
    public event: OrderFilterResultsChangedEvent;

    public constructor(batchUpdateInProgress: boolean) {
        this.event = {
            changedType: OrderFilterResultsChangedEventType.UPDATED,
            batchUpdateInProgress,
            updates: {
                newMembers: [],
                removedMembers: [],
                updatedMembers: [],
            },
        };
    }

    public newMember(
        idx: number,
        update: OrderFilterResultsChangedEventMemberUpdate
    ): void {
        this.event.updates!.newMembers[idx] = update;
    }

    public removedMember(
        idx: number,
        update: OrderFilterResultsChangedEventMemberUpdate
    ): void {
        this.event.updates!.removedMembers[idx] = update;
    }

    public updatedMember(
        idx: number,
        update: OrderFilterResultsChangedEventMemberUpdate
    ): void {
        this.event.updates!.updatedMembers[idx] = update;
    }

    public get haveChanges() {
        return (
            this.event.updates!.updatedMembers.length > 0 ||
            this.event.updates!.newMembers.length > 0 ||
            this.event.updates!.removedMembers.length > 0
        );
    }
}
